import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import UrgencyBanner from "../components/UrgencyBanner";
import { fetchProducts } from "../lib/api";
import { useCart } from "../lib/cart";
import useSeo from "../lib/useSeo";
import {
  ArrowLeft,
  Check,
  ShoppingBag,
  Sparkles,
  Wand2,
  Star,
  Truck,
  ShieldCheck,
  Heart,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

const LIBRARY_KEY = "platebuddies_custom_buddies_v1";

const ALL_BUDDIES = [
  { id: "pineapple-chicky", name: "Pineapple Chicky", image: "/buddies/pineapple-chicky.jpg" },
  { id: "captain-sammy", name: "Captain Sammy", image: "/buddies/captain-sammy.png" },
  { id: "maestro-meatball", name: "Maestro Meatball", image: "/buddies/maestro-meatball.jpg" },
  { id: "sally-salad", name: "Sally Salad", image: "/buddies/sally-salad.png" },
  { id: "sir-ampm", name: "Sir AM/PM", image: "/buddies/sir-ampm.png" },
  { id: "queso-kid", name: "Queso Kid", image: "/buddies/queso-kid.png" },
];

const DETAIL = {
  "starter-kit": {
    productId: "starter_kit",
    badge: "Best for first-timers",
    accent: "#FFD54F",
    bg: "#FFF6D6",
    hero: "/brand/starter-hero.jpg",
    heroAlt: "Plate Buddies Starter Kit flat-lay with all six Buddy mats, clear play-plate, day-of-the-week stickers, and Create Your Own card",
    eyebrow: "The Starter Kit",
    title: "Mealtime Magic Starter Kit",
    sub: "The full character-led mealtime experience for one happy little eater. Everything you need to turn dinner into an adventure.",
    storyTitle: "What's inside the box",
    contents: [
      { name: "Clear play-plate", detail: "BPA-free, top-rack dishwasher safe, just the right size for ages 2–7." },
      { name: "6 collectible Buddy mats", detail: "All six characters — Pineapple Chicky, Captain Sammy, Maestro Meatball, Sally Salad, Sir AM/PM, Queso Kid — slip under the plate so they peek through every bite." },
      { name: "Day-of-the-week stickers", detail: "Bright, glittery, hand-illustrated. Mon–Sun, perfect for a fridge or planning wall." },
      { name: "“Create Your Own Buddy” coloring card", detail: "Grab the crayons and design your kid's first hand-drawn Buddy together." },
      { name: "Digital meal planner access", detail: "Free access to our magnetic-style weekly planner with auto-generated shopping lists." },
      { name: "Parent quick-start guide", detail: "How to introduce the system, plus 7 calmer-dinner rituals that take 60 seconds each." },
    ],
    benefits: [
      "Less stress at the dinner table",
      "Real kid participation, not bribery",
      "Screen-free family connection",
      "A routine that sticks",
    ],
    spec: [
      { label: "Ages", value: "2–7" },
      { label: "Plate material", value: "Food-safe BPA-free Tritan" },
      { label: "Mat material", value: "Wipe-clean food-safe vinyl" },
      { label: "Ships in", value: "1–2 business days" },
    ],
  },
  "premium-kit": {
    productId: "premium_kit",
    badge: "Most loved",
    accent: "#F26A34",
    bg: "#FFE3D8",
    hero: "/brand/premium-flat-lay.jpg",
    heroAlt: "Premium Plate Buddies Meal Planning System flat-lay with tempered glass plate, all 6 character mats and matching magnets",
    eyebrow: "The Premium Kit",
    title: "Premium Meal Planning System",
    sub: "Our full system — the Upgraded Tempered Glass Play Plate, all six Buddies as both mats AND matching magnets, plus access to the Buddy Generator.",
    storyTitle: "What's inside the box",
    contents: [
      { name: "Everything in the Starter Kit", detail: "All 6 Buddy mats, day-of-the-week stickers, coloring card, parent guide." },
      { name: "Upgraded Tempered Glass Play Plate", detail: "Heat-tempered, scratch-resistant, gorgeous on any table — the upgrade your family will keep for years." },
      { name: "6 matching Buddy magnets", detail: "Tiny printed magnets of every character — use them on the meal-planning board to plan the week visually." },
      { name: "Magnetic weekly meal-planning board", detail: "Day-of-the-week strips + Buddy magnets = your kid plans dinner with you in 90 seconds." },
      { name: "Access to the Buddy Generator", detail: "Design unlimited custom Buddies in your child's name — coordinated recipes auto-paired." },
      { name: "Family adventure journal", detail: "Capture firsts, favorites, and the funny things kids say about the food they finally tried." },
    ],
    benefits: [
      "Plans the whole week in one playful sit-down",
      "Tempered glass means it survives toddler life",
      "Unlimited custom Buddies for your fridge",
      "Becomes a family ritual, not a one-time toy",
    ],
    spec: [
      { label: "Ages", value: "2–7" },
      { label: "Plate material", value: "Heat-tempered glass" },
      { label: "Magnets", value: "6 character + 7 weekday" },
      { label: "Ships in", value: "1–2 business days" },
    ],
  },
  "custom-buddy": {
    productId: "buddy_print",
    badge: "Personalized",
    accent: "#B39DDB",
    bg: "#ECE5F7",
    hero: null,
    eyebrow: "Custom Buddy Print",
    title: "Your kid's creation. Made real.",
    sub: "Design a one-of-a-kind Plate Buddy in your child's own words. We ship it as a matching set — a 2.5\" fridge magnet and a Plate Buddy mat that slips under the clear plate. Ships in 5–7 business days.",
    storyTitle: "How it works",
    contents: [
      { name: "1. Make a Buddy", detail: "Describe it in your child's words — we illustrate it in seconds in the official Plate Buddies style." },
      { name: "2. We print the matching set", detail: 'A 2.5" fridge magnet AND a full Plate Buddy mat featuring your child\'s creation — printed at our studio.' },
      { name: "3. Coordinated recipe card", detail: "Every order ships with a kid-friendly recipe card themed to the Buddy you designed." },
      { name: "4. Ships in 5–7 business days", detail: "Tracked shipping. Free over $50." },
    ],
    benefits: [
      'A 2.5" magnet for the fridge or planning board',
      "Matching Plate Buddy mat to slip under the clear plate",
      "Unique recipe card coordinated to the Buddy",
      "Comes with generator access — design as many as you want",
    ],
    spec: [
      { label: "Magnet size", value: '2.5" round' },
      { label: "Mat size", value: '7" round, fits under our clear plate' },
      { label: "Recipe card", value: "Auto-coordinated, prints with every order" },
      { label: "Ships in", value: "5–7 business days" },
    ],
    isCustomPrint: true,
  },
};

function loadLibrary() {
  try {
    return JSON.parse(localStorage.getItem(LIBRARY_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeLibrary(list) {
  try {
    localStorage.setItem(LIBRARY_KEY, JSON.stringify(list));
  } catch {
    /* noop */
  }
}

export default function ProductDetail() {
  const { productId } = useParams();
  const meta = DETAIL[productId];
  const [products, setProducts] = useState([]);
  const [library, setLibrary] = useState(loadLibrary);
  const [pickedBuddyId, setPickedBuddyId] = useState(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, [productId]);
  useEffect(() => { fetchProducts().then(setProducts).catch((err) => console.warn("[fetch] failed:", err)); }, []);

  const product = useMemo(
    () => products.find((p) => p.id === meta?.productId),
    [products, meta?.productId]
  );

  // Per-page SEO for /products/:productId
  useSeo({
    title: meta
      ? `${meta.title} — Plate Buddies`
      : "Shop | Plate Buddies",
    description: meta?.sub || "Shop kid-friendly Plate Buddies kits, custom prints, and coloring pages.",
    image: meta?.hero ? `https://platebuddies.com${meta.hero}` : undefined,
    url: meta ? `https://platebuddies.com/products/${productId}` : undefined,
    type: "product",
  });

  if (!meta) {
    return (
      <div className="bg-pb-cream min-h-screen font-body">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display font-black text-3xl">Product not found</h1>
          <Link to="/shop" className="mt-6 inline-block text-pb-coral font-bold underline">Back to shop</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const removeFromLibrary = (id) => {
    const next = library.filter((b) => b.id !== id);
    writeLibrary(next);
    setLibrary(next);
    if (pickedBuddyId === id) setPickedBuddyId(null);
  };

  const addStandardToCart = () => {
    if (!product) return;
    addItem({ product_id: product.id, name: product.name, unit_price: product.amount });
    toast.success(`${product.name} added to cart!`);
    navigate("/cart");
  };

  const addCustomPrintToCart = () => {
    if (!product) return;
    const buddy = library.find((b) => b.id === pickedBuddyId);
    if (!buddy) {
      toast.error("Pick a Buddy from your library first.");
      return;
    }
    addItem({
      product_id: product.id,
      name: `Custom Buddy Print — ${buddy.name}`,
      unit_price: product.amount,
      custom_buddy_id: buddy.id,
      custom_buddy_name: buddy.name,
      custom_buddy_image: buddy.image,
    });
    toast.success(`${buddy.name} print added to cart!`);
    navigate("/cart");
  };

  // For the custom-buddy page: a CTA that adds an empty buddy_print to the cart
  // so the access gate's "intent unlock" lets them into /create.
  const startDesigning = () => {
    if (product) {
      // Don't add — just route. The gate also checks for buddy_print in cart, but the
      // generator entitlement isn't required for active shoppers if their next step is creating.
      // We use a temporary-intent product line item instead.
      addItem({
        product_id: product.id,
        name: "Custom Buddy Print (designing)",
        unit_price: product.amount,
      });
    }
    navigate("/create");
  };

  return (
    <div data-testid={`page-product-${productId}`} className="font-body bg-pb-cream min-h-screen">
      {/* Rich-result Product schema — helps Google surface price, availability,
          and offer badge in search results. Falls back to the static catalog
          if the /api fetch hasn't resolved yet. */}
      {product && (
        <JsonLd
          id={`product-${productId}`}
          data={{
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name || meta.title,
            description: meta.subtitle || product.description || meta.title,
            image: meta.hero ? [`https://platebuddies.com${meta.hero}`] : [
              "https://platebuddies.com/brand/og-share-v3.jpg",
            ],
            brand: { "@type": "Brand", "name": "Plate Buddies" },
            sku: product.id,
            offers: {
              "@type": "Offer",
              url: `https://platebuddies.com/products/${productId}`,
              priceCurrency: "USD",
              price: Number(product.amount || 0).toFixed(2),
              availability: "https://schema.org/InStock",
              itemCondition: "https://schema.org/NewCondition",
              seller: { "@type": "Organization", "name": "Plate Buddies" },
            },
          }}
        />
      )}
      <UrgencyBanner />
      <Navbar />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-32 lg:pb-20">
        <Link
          to="/shop"
          data-testid="link-back-shop"
          className="inline-flex items-center gap-2 text-sm font-bold text-pb-soft hover:text-pb-coral transition"
        >
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>

        <div className="mt-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Imagery / hero */}
          <div className="relative">
            <div
              className="rounded-[2.5rem] overflow-hidden border-4 border-white shadow-[0_30px_80px_rgba(28,22,40,0.12)] relative"
              style={{ background: meta.bg }}
            >
              {meta.hero ? (
                <img src={meta.hero} alt={meta.heroAlt} className="w-full h-auto" />
              ) : (
                <div className="aspect-square flex items-center justify-center p-12">
                  <div className="bg-white rounded-3xl p-10 shadow-lg text-center">
                    <Wand2 className="h-14 w-14 mx-auto text-pb-coral" />
                    <p className="mt-3 font-display font-black text-xl">Custom-made by you</p>
                    <p className="text-pb-soft text-sm mt-1">Design it. We print it.</p>
                  </div>
                </div>
              )}
              {product?.compare_at && product.compare_at > product.amount && (
                <span className="absolute top-4 left-4 bg-pb-coral text-white text-xs font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full shadow">
                  Save ${(product.compare_at - product.amount).toFixed(0)}
                </span>
              )}
            </div>

            {/* Trust strip */}
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <TrustChip icon={Truck} label="Free shipping over $50" />
              <TrustChip icon={ShieldCheck} label="30-day happiness promise" />
              <TrustChip icon={Heart} label="Built by a mom for kids 2–7" />
            </div>
          </div>

          {/* Right column — copy + buy */}
          <div>
            <span
              className="inline-block text-[10px] font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full text-pb-ink"
              style={{ background: meta.accent }}
            >
              {meta.badge}
            </span>
            <p className="mt-3 text-sm font-extrabold uppercase tracking-widest text-pb-coral">{meta.eyebrow}</p>
            <h1 data-testid="product-title" className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">
              {meta.title}
            </h1>
            <p className="mt-4 text-pb-soft text-lg leading-relaxed">{meta.sub}</p>

            {/* Price + CTA */}
            <div className="mt-7 flex items-baseline gap-3">
              {product ? (
                <>
                  <span className="font-display font-black text-5xl">${product.amount}</span>
                  {product.compare_at && product.compare_at > product.amount && (
                    <span className="text-pb-soft text-lg font-bold line-through">${product.compare_at}</span>
                  )}
                </>
              ) : (
                <div className="h-10 w-32 bg-pb-cream rounded animate-pulse" />
              )}
            </div>

            {/* Custom-print: pick from library */}
            {meta.isCustomPrint && (
              <div className="mt-7 rounded-3xl bg-white p-5 border-2 border-pb-sky/30 shadow-[0_14px_32px_rgba(28,22,40,0.06)]">
                <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
                  Step 1 · Pick a Buddy from your library
                </p>
                {library.length === 0 ? (
                  <div data-testid="library-empty" className="mt-4 rounded-2xl border-2 border-dashed border-pb-sky/50 p-6 text-center">
                    <Sparkles className="h-8 w-8 mx-auto text-pb-coral" />
                    <p className="mt-2 font-display font-black">No Buddies designed yet</p>
                    <p className="text-pb-soft text-sm mt-1">
                      Open the Buddy Generator to design one in your kid's name — then come back to print it.
                    </p>
                    <button
                      data-testid="btn-start-designing"
                      onClick={startDesigning}
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-6 py-3 hover:scale-105 transition shadow-[0_10px_24px_rgba(242,106,52,0.35)]"
                    >
                      <Wand2 className="h-4 w-4" /> Design my first Buddy
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {library.map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          data-testid={`library-pick-${b.id}`}
                          onClick={() => setPickedBuddyId(b.id)}
                          className={`relative rounded-2xl overflow-hidden border-4 transition ${
                            pickedBuddyId === b.id
                              ? "border-pb-coral scale-105 shadow-[0_10px_24px_rgba(242,106,52,0.3)]"
                              : "border-white opacity-80 hover:opacity-100"
                          }`}
                          style={{ background: b.bg || "#FFF6D6" }}
                        >
                          <img src={b.image} alt={b.name} className="w-full aspect-square object-cover" />
                          <p className="text-[10px] font-extrabold text-pb-ink px-1 py-1 truncate bg-white">{b.name}</p>
                          {pickedBuddyId === b.id && (
                            <span className="absolute top-1 right-1 bg-pb-coral text-white rounded-full p-1">
                              <Check className="h-3 w-3" />
                            </span>
                          )}
                          <span
                            onClick={(e) => { e.stopPropagation(); removeFromLibrary(b.id); }}
                            role="button"
                            className="absolute top-1 left-1 bg-white/90 text-pb-soft hover:text-pb-coral rounded-full p-1 cursor-pointer"
                            title="Remove from library"
                          >
                            <Trash2 className="h-3 w-3" />
                          </span>
                        </button>
                      ))}
                      <button
                        type="button"
                        data-testid="btn-design-more"
                        onClick={startDesigning}
                        className="rounded-2xl border-4 border-dashed border-pb-sky/60 hover:border-pb-coral bg-pb-cream text-pb-soft hover:text-pb-coral font-extrabold flex flex-col items-center justify-center aspect-square transition"
                      >
                        <Plus className="h-5 w-5" />
                        <span className="text-[10px] uppercase tracking-widest mt-0.5">Make one</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {meta.isCustomPrint ? (
              <button
                data-testid="btn-add-custom-print"
                onClick={addCustomPrintToCart}
                disabled={!product || !pickedBuddyId}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-8 py-4 shadow-[0_14px_32px_rgba(242,106,52,0.35)] hover:scale-[1.02] transition disabled:opacity-50 disabled:hover:scale-100"
              >
                <ShoppingBag className="h-5 w-5" />
                {pickedBuddyId ? `Add to cart — $${product?.amount}` : "Pick a Buddy first"}
              </button>
            ) : (
              <button
                data-testid="btn-add-to-cart"
                onClick={addStandardToCart}
                disabled={!product}
                className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-8 py-4 shadow-[0_14px_32px_rgba(242,106,52,0.35)] hover:scale-[1.02] transition disabled:opacity-50"
              >
                <ShoppingBag className="h-5 w-5" />
                Add to cart {product && `— $${product.amount}`}
              </button>
            )}

            {/* Quick benefits */}
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-4 gap-y-2.5">
              {meta.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm font-bold text-pb-ink">
                  <Check className="h-4 w-4 text-pb-coral mt-0.5 flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* What's inside */}
        <div className="mt-20 grid lg:grid-cols-3 gap-10">
          <div>
            <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral">{meta.storyTitle}</p>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-2 leading-tight">
              {meta.isCustomPrint ? "From idea to fridge." : "Built so kids can\u2019t wait to open it."}
            </h2>
            <p className="mt-4 text-pb-soft">
              {meta.isCustomPrint
                ? "A keepsake your kid actually wants to use. Magnet on the back, recipe card included, ships from our studio."
                : "Everything packed into one warm, well-illustrated box. Designed to look as good on your counter as it works at dinner."}
            </p>
          </div>
          <div className="lg:col-span-2 space-y-3">
            {meta.contents.map((c, i) => (
              <div
                key={c.name}
                data-testid={`contents-${i}`}
                className="rounded-2xl bg-white p-5 border-2 border-white shadow-[0_8px_20px_rgba(28,22,40,0.05)] flex gap-4"
              >
                <div
                  className="h-11 w-11 rounded-2xl flex items-center justify-center font-display font-black text-white flex-shrink-0"
                  style={{ background: meta.accent }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1">
                  <p className="font-display font-black text-lg leading-tight">{c.name}</p>
                  <p className="text-pb-soft text-sm mt-1 leading-relaxed">{c.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Buddies you'll meet (skip on custom-print) */}
        {!meta.isCustomPrint && (
          <div className="mt-20">
            <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral text-center">Inside, you'll find</p>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-2 leading-tight text-center">
              All six Buddies, ready for tonight's adventure.
            </h2>
            <div className="mt-10 grid grid-cols-3 sm:grid-cols-6 gap-3 max-w-4xl mx-auto">
              {ALL_BUDDIES.map((b) => (
                <div key={b.id} className="text-center">
                  <div className="aspect-square rounded-full overflow-hidden border-4 border-white shadow-md bg-pb-cream">
                    <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                  </div>
                  <p className="text-xs font-extrabold mt-2 leading-tight">{b.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Specs */}
        <div className="mt-20 rounded-[2rem] bg-white p-8 border border-pb-sky/30 shadow-[0_14px_32px_rgba(28,22,40,0.06)]">
          <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral">The details</p>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
            {meta.spec.map((s) => (
              <div key={s.label}>
                <p className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">{s.label}</p>
                <p className="font-display font-black text-lg mt-1">{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <div className="inline-flex items-center gap-1 text-pb-sun">
            {[1,2,3,4,5].map((i) => <Star key={i} className="h-4 w-4 fill-pb-sun" />)}
          </div>
          <p className="font-display font-black text-2xl mt-3">Mealtime, made playful — for under $40.</p>
          <p className="text-pb-soft mt-1">Pay safely with Stripe. Cards, Apple Pay, Google Pay.</p>
          <button
            data-testid="btn-cta-bottom"
            onClick={meta.isCustomPrint ? addCustomPrintToCart : addStandardToCart}
            disabled={!product || (meta.isCustomPrint && !pickedBuddyId)}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-8 py-4 shadow-[0_14px_32px_rgba(242,106,52,0.35)] hover:scale-105 transition disabled:opacity-50"
          >
            <ShoppingBag className="h-4 w-4" />
            {meta.isCustomPrint
              ? (pickedBuddyId ? `Add to cart — $${product?.amount}` : "Pick a Buddy first")
              : `Add to cart — $${product?.amount}`}
          </button>
        </div>
      </section>

      {/* Sticky mobile add-to-cart bar — on <lg viewports the buy CTA is
       * pushed way below the fold by the stacked image + trust badges. This
       * fixed bottom bar keeps the price + "Add to cart" reachable from any
       * scroll position, dramatically reducing mobile bounce. */}
      {product && (
        <div
          data-testid="sticky-mobile-buy"
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t-2 border-pb-sky/30 shadow-[0_-8px_28px_rgba(28,22,40,0.10)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
        >
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-soft leading-none">
                {meta.eyebrow}
              </p>
              <p className="font-display font-black text-xl text-pb-ink leading-tight">
                ${product.amount}
                {product.compare_at && product.compare_at > product.amount && (
                  <span className="text-pb-soft text-sm font-bold line-through ml-2">${product.compare_at}</span>
                )}
              </p>
            </div>
            <button
              data-testid="btn-mobile-buy"
              onClick={meta.isCustomPrint ? addCustomPrintToCart : addStandardToCart}
              disabled={meta.isCustomPrint && !pickedBuddyId}
              className="flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-6 py-3.5 shadow-[0_10px_24px_rgba(242,106,52,0.35)] hover:scale-105 transition disabled:opacity-50 touch-manipulation"
            >
              <ShoppingBag className="h-4 w-4" />
              {meta.isCustomPrint && !pickedBuddyId ? "Pick a Buddy" : "Add to cart"}
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function TrustChip({ icon: Icon, label }) {
  return (
    <div className="rounded-2xl bg-white px-3 py-3 border border-pb-sky/30 flex flex-col items-center gap-1.5">
      <Icon className="h-4 w-4 text-pb-coral" />
      <span className="text-[11px] font-bold text-pb-ink leading-tight">{label}</span>
    </div>
  );
}
